// Calibration reducers for the Polaris slice: storing the sample, moving the
// carousel and tracking user-started sample builds. Split out of polarisSlice
// to keep that file within the repo's per-file size budget.
import {ActionReducerMapBuilder, isAnyOf} from "@reduxjs/toolkit";
import type {TCalibrationResponse} from "api/polarisApi/types";
import type {TPolarisReducerState} from "store/polaris/types";
import {calibrationRoundOf, isCalibrationSampleTimeout} from "./calibrationGeneration";
import {castVote, loadCalibration, resample, retryCalibration} from "./polarisThunks";

// Stores a GET-shaped calibration payload (load or retry).
const applyCalibration = (state: TPolarisReducerState, payload: TCalibrationResponse) => {
	const previous = state.calibration;
	const round = calibrationRoundOf(payload);
	// A background re-read (fallback poll, chat refresh) of the round already
	// on screen must not move the user back in the carousel.
	const sameRound =
		round !== null &&
		(previous?.candidates.length ?? 0) > 0 &&
		calibrationRoundOf(previous) === round;

	state.calibration = payload;

	// A sample that arrives late clears an earlier "did not arrive" state.
	if (payload.candidates.length > 0 && state.calibration_sampling === "failed") {
		state.calibration_sampling = "idle";
	}

	if (sameRound && state.active_calibration_index < payload.candidates.length) return;

	// A fresh round can arrive with carried-forward candidates already
	// voted at positions 1..k — open on the first actually-unvoted one.
	const nextUnvoted = payload.candidates.findIndex((c) => c.vote === null);

	state.active_calibration_index = nextUnvoted >= 0 ? nextUnvoted : 0;
};

// Matchers only, so the slice can register them after all its addCase calls.
export const addCalibrationMatchers = (builder: ActionReducerMapBuilder<TPolarisReducerState>) => {
	builder
		.addMatcher(isAnyOf(loadCalibration.fulfilled), (state, action) => {
			applyCalibration(state, action.payload);
		})
		// resample.fulfilled stores nothing — the thunk re-dispatches
		// loadCalibration, whose fulfilled case stores the fresh sample.
		.addMatcher(isAnyOf(castVote.fulfilled), (state, action) => {
			// The vote endpoint returns the full calibration payload
			state.calibration = action.payload;

			// Auto-advance the carousel to the next unvoted candidate
			const nextUnvoted = action.payload.candidates.findIndex((c) => c.vote === null);

			if (nextUnvoted >= 0) state.active_calibration_index = nextUnvoted;
		})
		// A user-started sample build: loading while it runs; failed when the
		// sample did not arrive in time (or Try again itself failed).
		.addMatcher(isAnyOf(resample.pending, retryCalibration.pending), (state) => {
			state.calibration_sampling = "loading";
		})
		.addMatcher(isAnyOf(resample.fulfilled), (state) => {
			state.calibration_sampling = "idle";
		})
		.addMatcher(isAnyOf(resample.rejected), (state, action) => {
			state.calibration_sampling = isCalibrationSampleTimeout(action.error)
				? "failed"
				: "idle";
		})
		.addMatcher(isAnyOf(retryCalibration.fulfilled), (state, action) => {
			state.calibration_sampling = "idle";
			applyCalibration(state, action.payload);
		})
		.addMatcher(isAnyOf(retryCalibration.rejected), (state) => {
			state.calibration_sampling = "failed";
		});
};
